import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

// App-wide safety net: a thrown render error in any page used to blank the
// ENTIRE console (white screen, no recovery). This contains the blast radius
// to the main content area and offers a recover action. `resetKey` (the active
// nav id) auto-clears the error when the user navigates elsewhere.
export class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface for debugging; the boundary already rendered the fallback.
    console.error('[PageErrorBoundary]', error?.message || error, info?.componentStack?.split('\n').slice(0, 4).join(' | '));
  }

  componentDidUpdate(prevProps) {
    // Navigating to a different surface clears the error so the app recovers.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="bg-white border border-[#FECACA] rounded-[12px] px-6 py-10 text-center max-w-[640px] mx-auto mt-6">
          <div className="w-10 h-10 rounded-full bg-[#FEF2F2] text-[#9B1F1F] flex items-center justify-center mx-auto mb-3">
            <AlertTriangle className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div className="text-[15px] font-semibold text-[#1F2937] mb-1">This section hit an error</div>
          <div className="text-[12.5px] text-[#6B7280] max-w-[480px] mx-auto mb-4">
            The rest of the console is fine — switch to another surface, or reload. The error has been logged to the console.
          </div>
          {this.state.error?.message && (
            <div className="text-[11px] font-mono text-[#9B1F1F] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-3 py-2 mb-4 text-left overflow-x-auto">
              {String(this.state.error.message).slice(0, 300)}
            </div>
          )}
          <button onClick={() => this.setState({ error: null })} className="h-9 px-4 rounded-md text-[12.5px] font-medium bg-[#1F2937] hover:bg-[#111827] text-white inline-flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" />Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
